import React from "react";
import { Provider } from "react-redux";
import * as checkoutAPI from "tsi-common-react/src/api/checkout";
import { registerCheckoutCascades } from "tsi-common-react/src/apps/checkout/cascades-checkout";
import { registerCommonCascades } from "tsi-common-react/src/apps/checkout/cascades-main";
import { BasketLineVariant } from "tsi-common-react/src/apps/checkout/constants";
import { BasketMenuIcon } from "tsi-common-react/src/apps/checkout/containers/BasketMenuIcon";
import { Dispatchers as CheckoutDispatchers } from "tsi-common-react/src/apps/checkout/dispatchers";
import { Loaders as CheckoutLoaders } from "tsi-common-react/src/apps/checkout/loaders";
import {
    openApplyModal,
    openPreQualModal,
} from "tsi-common-react/src/apps/financing/reducers";
import * as signals from "tsi-common-react/src/apps/signals";
import { isoWebPageURL } from "tsi-common-react/src/models/nominals";
import { onReadyStateComplete } from "tsi-common-react/src/utils/events";
import { strToBool } from "tsi-common-react/src/utils/format";
import { dynamicPlaceComponent } from "tsi-common-react/src/utils/react";
import {
    getPageSetting,
    isPageInsideIFrame as isPageInsideIFrameCheck,
} from "tsi-common-react/src/utils/settings";
import { urls } from "tsi-common-react/src/utils/urls";

import { reverse } from "@reactivated";

import { financingCCs } from "../../../../../libs/tsi-common-react/src/apps/checkout/components/FinancingModalTriggerFinance";
import {
    FinancingCardType,
    FinancingModalTriggerTheme,
} from "../../../../../libs/tsi-common-react/src/constants";
import { persistor, rehydratingStore, store } from "../store";
import { FinancingModalTriggerRetailModalFinance } from "./components/FinancingModalTriggerRetailModalFinance";
import { FinancingModalTriggerRetailModalPreapproval } from "./components/FinancingModalTriggerRetailModalPreapproval";

// ============================================================================
// Setup Redux Store / Redux Event Dispatchers
// ============================================================================
export const dispatchers = new CheckoutDispatchers(store.dispatch);
export const loaders = new CheckoutLoaders(dispatchers);

// ============================================================================
// Component builder functions used by other applications
// ============================================================================
export const buildRetailModalPreapprovalTrigger = (
    onRequestParentClose: () => void,
) => {
    return (
        <Provider store={store}>
            <FinancingModalTriggerRetailModalPreapproval
                onRequestParentClose={onRequestParentClose}
            />
        </Provider>
    );
};

export const buildRetailModalFinancingTrigger = () => {
    return (
        <Provider store={store}>
            <FinancingModalTriggerRetailModalFinance />
        </Provider>
    );
};

// ============================================================================
// Render checkout app components
// ============================================================================
/* eslint-disable-next-line no-async-promise-executor */
const componentsRendering = rehydratingStore.then(() => {
    // Register store cascades
    registerCommonCascades(store);

    // Render Pre Approval Triggers for Homepage
    dynamicPlaceComponent(
        '[data-place-react="financing-modal-trigger--block"]',
        async (elem) => {
            const { FinancingModalTriggerBlock } = await import(
                "tsi-common-react/src/apps/financing/FinancingModalTriggerBlock"
            );
            // TODO(i18n): Default string should be translated or use non-user-facing identifier
            const applicationSource =
                elem.dataset.applicationSource || "Financing Page";
            return (
                <Provider store={store}>
                    <FinancingModalTriggerBlock
                        cardType={FinancingCardType.TEMPUR_CC}
                        theme={
                            (elem.dataset
                                .theme as FinancingModalTriggerTheme) ||
                            FinancingModalTriggerTheme.DEFAULT
                        }
                        aprInfoID={"#apr-information"}
                        applicationSource={applicationSource}
                        financeLink={
                            urls.pageURL("finance-link") || urls.pageURL("home")
                        }
                    />
                </Provider>
            );
        },
    ).catch(console.error);

    dynamicPlaceComponent(
        '[data-place-react="financing-modal-trigger--homepage"]',
        async (elem) => {
            const { FinancingModalTriggerHomepage } = await import(
                "./components/FinancingModalTriggerHomepage"
            );
            // TODO(i18n): Default string should be translated or use non-user-facing identifier
            const applicationSource =
                elem.dataset.applicationSource || "Toolbox";
            return (
                <Provider store={store}>
                    <FinancingModalTriggerHomepage
                        applicationSource={applicationSource}
                    />
                </Provider>
            );
        },
    ).catch(console.error);

    // Render Pre Approval Trigger for Finance Page Header
    dynamicPlaceComponent(
        '[data-place-react="financing-modal-trigger--check-now"]',
        async (elem) => {
            const { FinancingModalTriggerCheckNow } = await import(
                "./components/FinancingModalTriggerCheckNow"
            );
            const classNames = elem.dataset.extraClasses as string;
            return (
                <Provider store={store}>
                    <FinancingModalTriggerCheckNow classString={classNames} />
                </Provider>
            );
        },
    ).catch(console.error);

    // Render Pre Approval Trigger for Finance Page
    dynamicPlaceComponent(
        '[data-place-react="financing-modal-trigger--finance-page"]',
        async () => {
            const { FinancingModalTriggerFinance } = await import(
                "tsi-common-react/src/apps/checkout/components/FinancingModalTriggerFinance"
            );
            return (
                <Provider store={store}>
                    {/* TODO(i18n): Hardcoded string should be translated or use non-user-facing identifier */}
                    <FinancingModalTriggerFinance
                        applicationSource="Financing Page"
                        ccSource={financingCCs.TEMPUR}
                    />
                </Provider>
            );
        },
    ).catch(console.error);

    // Render Calculator on Finance Page
    dynamicPlaceComponent(
        '[data-place-react="finance-calculator"]',
        async () => {
            const { FinanceCalculator } = await import(
                "./components/FinanceCalculator"
            );
            return (
                <Provider store={store}>
                    <FinanceCalculator />
                </Provider>
            );
        },
    ).catch(console.error);

    // Render basket menu icons
    dynamicPlaceComponent(
        '[data-place-react="basket-menu-icon"]',
        async (elem) => {
            const { FinancingModalTriggerMinicart } = await import(
                "./components/FinancingModalTriggerMinicart"
            );
            const className = elem.dataset.extraClasses;
            const enableBasketPrequalSDK = strToBool(
                getPageSetting("show-prequal-cart"),
            );
            const basketURL = isoWebPageURL.wrap(reverse("basket:summary"));
            const checkoutURL = isoWebPageURL.wrap(reverse("checkout:index"));
            const isPageInsideIFrame = isPageInsideIFrameCheck();
            return (
                <Provider store={store}>
                    <BasketMenuIcon
                        className={className}
                        basketURL={basketURL}
                        checkoutURL={checkoutURL}
                        isPageInsideIFrame={isPageInsideIFrame}
                        basketLineVariant={BasketLineVariant.MINIMAL_ENHANCED}
                        buildPromoComponents={(closeModal) => {
                            if (!enableBasketPrequalSDK) {
                                return null;
                            }
                            return (
                                <Provider store={store}>
                                    <FinancingModalTriggerMinicart
                                        closeModal={closeModal}
                                    />
                                </Provider>
                            );
                        }}
                    />
                </Provider>
            );
        },
    ).catch(console.error);

    // Render Basket Application
    dynamicPlaceComponent("#basket-app", async () => {
        const { Basket } = await import(
            "tsi-common-react/src/apps/checkout/containers/Basket"
        );
        const { getLineProductUpsell } = await import(
            "./components/BasketLineSuggestions"
        );
        const { BasketSidebarContent } = await import(
            "./components/BasketSidebarContent"
        );
        const { BasketFinePrint } = await import(
            "./components/BasketFinePrint"
        );
        const disableFreeShippingMessage = strToBool(
            getPageSetting("disable-basket-free-shipping-message"),
        );
        return (
            <Provider store={store}>
                <Basket
                    getLineProductUpsell={getLineProductUpsell}
                    showValueProps={true}
                    getExtraSummaryContent={() => {
                        return null;
                    }}
                    getExtraSidebarContent={(data, isMobile) => {
                        const cartID = data.basket
                            ? data.basket.encoded_basket_id
                            : "";
                        return (
                            <BasketSidebarContent
                                plans={data.financing_plans}
                                isMobile={isMobile}
                                cartID={cartID}
                            />
                        );
                    }}
                    getExtraMainColumnContent={(data, isMobile) => {
                        if (isMobile) {
                            return null;
                        }
                        return <BasketFinePrint plans={data.financing_plans} />;
                    }}
                    disableFreeShippingMessage={disableFreeShippingMessage}
                    predictedDeliveryDates={getPageSetting(
                        "predicted-delivery-date-display-type",
                    )}
                    preferredDeliveryDates={getPageSetting(
                        "preferred-delivery-date-display-type",
                    )}
                />
            </Provider>
        );
    }).catch(console.error);

    // Render Configure Gifts with Purchase Application
    dynamicPlaceComponent("#configure-gifts-with-purchase-app", async () => {
        const { ConfigureGiftsWithPurchase } = await import(
            "tsi-common-react/src/apps/checkout/containers/ConfigureGiftsWithPurchase"
        );
        return (
            <Provider store={store}>
                <ConfigureGiftsWithPurchase />
            </Provider>
        );
    }).catch(console.error);

    // Render Checkout Application
    dynamicPlaceComponent("#checkout-app", async () => {
        registerCheckoutCascades(store);
        const { Checkout } = await import(
            "tsi-common-react/src/apps/checkout/containers/Checkout"
        );
        const { PreScreenModal } = await import("./components/PreScreenModal");
        const { CheckoutSidebarContent } = await import(
            "./components/CheckoutSidebarContent"
        );
        const { FinancingUpsellBlock } = await import(
            "tsi-common-react/src/apps/checkout/containers/FinancingUpsellBlock"
        );
        const { FortivaFinancingUpsellBlock } = await import(
            "tsi-common-react/src/apps/checkout/containers/FortivaFinancingUpsellBlock"
        );
        const disableFreeShippingMessage = strToBool(
            getPageSetting("disable-basket-free-shipping-message"),
        );
        return (
            <Provider store={store}>
                <Checkout
                    showFinancingPreQual={strToBool(
                        getPageSetting("enable-financing"),
                    )}
                    enableSplitPay={strToBool(
                        getPageSetting("enable-split-pay"),
                    )}
                    buildPreScreenModal={(props) => {
                        return <PreScreenModal {...props} />;
                    }}
                    getExtraSummaryContent={() => {
                        return null;
                    }}
                    getExtraSidebarContent={() => {
                        return <CheckoutSidebarContent />;
                    }}
                    buildFinancingUpsell={(grandTotal) => {
                        return <FinancingUpsellBlock grandTotal={grandTotal} />;
                    }}
                    buildFortivaFinancingUpsell={(grandTotal) => {
                        return (
                            <FortivaFinancingUpsellBlock
                                grandTotal={grandTotal}
                            />
                        );
                    }}
                    disableFreeShippingMessage={disableFreeShippingMessage}
                    predictedDeliveryDates={getPageSetting(
                        "predicted-delivery-date-display-type",
                    )}
                    preferredDeliveryDates={getPageSetting(
                        "preferred-delivery-date-display-type",
                    )}
                />
            </Provider>
        );
    }).catch(console.error);

    // Render Complete Deferred Payment Application
    dynamicPlaceComponent("#complete-deferred-payment-app", async (elem) => {
        registerCheckoutCascades(store);
        const { CompleteDeferredPayment } = await import(
            "tsi-common-react/src/apps/checkout/containers/CompleteDeferredPayment"
        );
        const { FinancingUpsellBlock } = await import(
            "tsi-common-react/src/apps/checkout/containers/FinancingUpsellBlock"
        );
        const disableFreeShippingMessage = strToBool(
            getPageSetting("disable-basket-free-shipping-message"),
        );
        return (
            <Provider store={store}>
                <CompleteDeferredPayment
                    initDataJSON={elem.dataset.initData || ""}
                    enableSplitPay={strToBool(
                        getPageSetting("enable-split-pay"),
                    )}
                    buildFinancingUpsell={(grandTotal) => {
                        return <FinancingUpsellBlock grandTotal={grandTotal} />;
                    }}
                    disableFreeShippingMessage={disableFreeShippingMessage}
                />
            </Provider>
        );
    }).catch(console.error);

    // Render Message Banner
    dynamicPlaceComponent(
        '[data-place-react="message-banner"]',
        async (elem) => {
            const { MessageBanner } = await import(
                "tsi-common-react/src/apps/checkout/containers/MessageBanner"
            );
            const tags = elem.dataset.tags || "";
            const message = elem.dataset.message || "";
            return (
                <Provider store={store}>
                    <MessageBanner tags={tags} message={message} />
                </Provider>
            );
        },
    ).catch(console.error);

    // Render Financing Form Application
    dynamicPlaceComponent(
        '[data-place-react="financing-form"]',
        async (elem) => {
            const { FinancingModalTrigger } = await import(
                "tsi-common-react/src/apps/financing/FinancingModalTrigger"
            );
            const linkClass =
                elem.dataset.linkClass ||
                "financing__offer__cta--apply al-financing-page__apply-now-button";
            // TODO(i18n): Default string should use t tagged template: t`Apply Now`
            const linkTitle = elem.dataset.linkTitle || "Apply Now";
            // TODO(i18n): Default string should use t tagged template: t`Apply Now`
            const linkText = elem.dataset.linkText || "Apply Now";
            // TODO(i18n): Default string should be translated or use non-user-facing identifier
            const applicationSource =
                elem.dataset.applicationSource || "Financing Page";
            return (
                <Provider store={store}>
                    <FinancingModalTrigger
                        modalType="full-app"
                        className={linkClass}
                        title={linkTitle}
                        applicationSource={applicationSource}
                    >
                        {linkText}
                    </FinancingModalTrigger>
                </Provider>
            );
        },
    ).catch(console.error);

    // Render Offer images
    dynamicPlaceComponent('[data-place-react="order-offer"]', async (elem) => {
        const desktop_image = elem.dataset.desktop_image || "";
        const mobile_image = elem.dataset.mobile_image || "";
        const offer_freq = elem.dataset.offer_freq || "";
        const benefit_value = elem.dataset.benefit_value || "";
        const offer_name = elem.dataset.offer_name || "";

        const { OrderOfferImage } = await import(
            "./components/OrderOfferImage"
        );
        const { OrderOffer } = await import("./components/OrderOffer");
        const appSlugName = getPageSetting("app-slug");
        if (appSlugName !== "tempurpedic") {
            return null;
        }

        if (!!offer_freq && !!benefit_value) {
            return (
                <Provider store={store}>
                    <OrderOffer
                        offer_freq={offer_freq}
                        benefit_value={benefit_value}
                        desktop_image={desktop_image}
                        mobile_image={mobile_image}
                        offer_name={offer_name}
                    />
                </Provider>
            );
        }
        if (!!desktop_image && !!mobile_image) {
            return (
                <Provider store={store}>
                    <OrderOfferImage
                        desktop_image={desktop_image}
                        mobile_image={mobile_image}
                    />
                </Provider>
            );
        }
        return null;
    }).catch(console.error);

    // Render Create Account Widgets
    dynamicPlaceComponent(
        '[data-place-react="create-account"]',
        async (elem) => {
            const { CreateAccount } = await import(
                "tsi-common-react/src/apps/checkout/containers/CreateAccount"
            );
            const firstName = elem.dataset.firstName || "";
            const email = elem.dataset.email || "";
            const currentUserID = elem.dataset.currentUserId;
            const orderUserID = elem.dataset.orderUserId;
            const orderUserHasPassword =
                (elem.dataset.orderUserHasPassword || "").toLowerCase() ===
                "true";
            const orderHistoryUrl = isoWebPageURL.wrap(
                elem.dataset.orderHistoryUrl || "",
            );
            const isAuthenticatedAsOrderOwner = currentUserID === orderUserID;
            return (
                <CreateAccount
                    firstName={firstName}
                    email={email}
                    isAuthenticatedAsOrderOwner={isAuthenticatedAsOrderOwner}
                    orderOwnerHasPassword={orderUserHasPassword}
                    orderHistoryURL={orderHistoryUrl}
                />
            );
        },
    ).catch(console.error);

    // Render Financing Modal (this is what opens in response to the
    // FinancingModalTriggers scattered across the site.)
    dynamicPlaceComponent('[data-place-react="financing-modal"]', async () => {
        const { FinancingModal } = await import(
            "tsi-common-react/src/apps/financing/FinancingModal"
        );
        return (
            <Provider store={store}>
                <FinancingModal />
            </Provider>
        );
    }).catch(console.error);
});

// ============================================================================
// Open Prequal modal when links contain the magic query params (thus preventing needless page refreshes)
// ============================================================================
Array.from(document.querySelectorAll<HTMLAnchorElement>("a[href]"))
    .map((elem): [URL, HTMLAnchorElement] => {
        const url = new URL(elem.href);
        return [url, elem];
    })
    .filter(([url]) => {
        return strToBool(`${url.searchParams.get("resume-prequal") || "no"}`);
    })
    .forEach(([_url, elem]) => {
        elem.addEventListener("click", (event) => {
            event.preventDefault();
            store.dispatch(openPreQualModal({}));
        });
    });

// ============================================================================
// Special On-Page-Load Functionality
// ============================================================================
const wipeCheckoutData = () => {
    // TODO(i18n): Log message is developer-facing, OK as-is
    console.debug("Erasing transient checkout data...");
    persistor.purge();
    checkoutAPI.clearShippingAddress();
    checkoutAPI.clearBillingAddress();
    loaders.resetFormData();
};

onReadyStateComplete.on(async () => {
    const url = new URL(window.location.href);

    // Wait for redux store to finish loading
    await rehydratingStore;
    await componentsRendering;

    // Redirect from basket page to homepage after 60 min delay
    const basketApp = document.getElementById("basket-app");
    if (basketApp) {
        setTimeout(() => {
            urls.navigateToHome();
        }, 3600_000);
    }

    // Auto open PreQual modal?
    const openPreQualModel = strToBool(
        `${url.searchParams.get("resume-prequal") || "no"}`,
    );
    if (openPreQualModel) {
        store.dispatch(openPreQualModal({}));
    }

    // Auto open Financing Application modal?
    const openCreditAppModel = strToBool(
        `${url.searchParams.get("open-credit-app") || "no"}`,
    );
    if (openCreditAppModel) {
        const appSource = `${url.searchParams.get("credit-app-source")}`;
        // TODO(i18n): Default string should be translated or use non-user-facing identifier
        store.dispatch(
            openApplyModal({
                applicationSource: appSource || "Financing Page",
            }),
        );
    }

    // Wipe checkout data?
    if (document.body.classList.contains("js-wipe-checkout-data")) {
        wipeCheckoutData();
    }
});

// ============================================================================
// CSR Toolbar Integration
// ============================================================================
const reloadCheckoutData = () => {
    wipeCheckoutData();
    // TODO(i18n): Log messages are developer-facing, OK as-is
    console.log("Reloading basket data");
    loaders.loadBasket();
    // TODO(i18n): Log messages are developer-facing, OK as-is
    console.log("Reloading shipping and billing address");
    loaders.loadShippingAndBillingAddresses();
};

signals.csr.onBeforeStartNewBasket.on(wipeCheckoutData);
signals.csr.onAfterStartAssistingCustomer.on(reloadCheckoutData);
signals.csr.onAfterStopAssistingCustomer.on(reloadCheckoutData);
